import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'EN' | 'ES';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  EN: {
    // Header
    'header.home': 'Home',
    'header.startDesigning': 'Start Designing',
    'header.myDesigns': 'My Designs',
    'header.signIn': 'Sign In',
    'header.signUp': 'Sign Up',
    'header.signOut': 'Sign Out',
    
    // Hero Section
    'hero.title': 'Welcome to Byrdson Services',
    'hero.subtitle': "Elevated homes designed for Florida's future. Visualize, customize, and design your resilient dream home in 3D before construction begins.",
    'hero.cta': 'Start Your Journey',
    'hero.featuresTitle': 'Why Choose Byrdson Services?',
    'hero.feature1Title': 'Choose Your Model',
    'hero.feature1Desc': 'Browse our collection of elevated home designs',
    'hero.feature2Title': 'Customize Everything',
    'hero.feature2Desc': 'Pick colors, materials, and finishes in real-time',
    'hero.feature3Title': 'Visualize in 3D',
    'hero.feature3Desc': 'See your dream home come to life before construction',
    
    
    // Start Designing
    'design.title': 'Start Designing Your Home',
    'design.subtitle': 'Customize your dream home with our interactive design tool. Choose finishes, colors, and layouts that match your style.',
    'design.modelSelection': 'Model Selection',
    'design.selectModel': 'Select a model to begin',
    'design.exteriorColors': 'Exterior Colors',
    'design.selectExterior': 'Choose your exterior color scheme',
    'design.siding': 'Siding',
    'design.trim': 'Trim',
    'design.doorsShutters': 'Doors & Shutters',
    'design.textures': 'Textures & Materials',
    'design.selectTexture': 'Select texture style',
    'design.roofing': 'Roofing Options',
    'design.selectRoofing': 'Choose your roof style',
    'design.flooring': 'Flooring Options',
    'design.vinylPlank': 'Vinyl Plank',
    'design.selectVinyl': 'Choose vinyl plank flooring',
    'design.carpetColor': 'Carpet Color',
    'design.selectCarpet': 'Choose carpet for bedrooms',
    'design.ceramicTile': 'Ceramic Tile',
    'design.selectCeramic': 'Choose bathroom floor tile',
    'design.interiorPaint': 'Interior Paint',
    'design.selectPaint': 'Choose interior paint color',
    'design.cabinetColor': 'Cabinet Color',
    'design.selectCabinet': 'Choose kitchen cabinet style',
    'design.countertop': 'Countertop',
    'design.selectCountertop': 'Choose countertop material',
    'design.summary': 'Design Summary',
    'design.summaryDesc': 'Review your selections',
    'design.notSelected': 'Not selected',
    'design.saveLater': 'Save for Later',
    'design.submit': 'Submit Design & Get Quote',
    'design.submitting': 'Saving...',
    'design.footerNote': 'Once submitted, our team will review your design and contact you within 24-48 hours with a detailed quote and timeline.',
    'design.toast.incomplete': 'Incomplete selection',
    'design.toast.incompleteDesc': 'Please complete all design choices before continuing.',
    'design.toast.saved': 'Design saved!',
    'design.toast.savedDesc': 'Your custom design preferences have been saved to your account.',
    'design.toast.error': 'Error saving design',
    'design.toast.errorDesc': 'There was a problem saving your design. Please try again.',
    'design.toast.authRequired': 'Authentication required',
    'design.toast.authRequiredDesc': 'Please sign in to save your design.',
    
    // Auth
    'auth.welcome': 'Welcome to Byrdson Services',
    'auth.signIn': 'Sign in to your account',
    'auth.signUp': 'Create a new account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.signInButton': 'Sign In',
    'auth.signUpButton': 'Sign Up',
    'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?',
    'auth.signUpLink': 'Sign up',
    'auth.signInLink': 'Sign in',
  },
  ES: {
    // Header
    'header.home': 'Inicio',
    'header.startDesigning': 'Comenzar Diseño',
    'header.myDesigns': 'Mis Diseños',
    'header.signIn': 'Iniciar Sesión',
    'header.signUp': 'Registrarse',
    'header.signOut': 'Cerrar Sesión',
    
    // Hero Section
    'hero.title': 'Bienvenido a Byrdson Services',
    'hero.subtitle': 'Casas elevadas diseñadas para el futuro de Florida. Visualiza, personaliza y diseña tu hogar resiliente de ensueño en 3D antes de comenzar la construcción.',
    'hero.cta': 'Comienza Tu Viaje',
    'hero.featuresTitle': '¿Por Qué Elegir Byrdson Services?',
    'hero.feature1Title': 'Elige Tu Modelo',
    'hero.feature1Desc': 'Explora nuestra colección de diseños de casas elevadas',
    'hero.feature2Title': 'Personaliza Todo',
    'hero.feature2Desc': 'Elige colores, materiales y acabados en tiempo real',
    'hero.feature3Title': 'Visualiza en 3D',
    'hero.feature3Desc': 'Ve tu casa de ensueño cobrar vida antes de la construcción',
    
    
    // Start Designing
    'design.title': 'Comienza a Diseñar Tu Casa',
    'design.subtitle': 'Personaliza la casa de tus sueños con nuestra herramienta de diseño interactiva. Elige acabados, colores y diseños que coincidan con tu estilo.',
    'design.modelSelection': 'Selección de Modelo',
    'design.selectModel': 'Selecciona un modelo para comenzar',
    'design.exteriorColors': 'Colores Exteriores',
    'design.selectExterior': 'Elige tu esquema de color exterior',
    'design.siding': 'Revestimiento',
    'design.trim': 'Moldura',
    'design.doorsShutters': 'Puertas y Persianas',
    'design.textures': 'Texturas y Materiales',
    'design.selectTexture': 'Selecciona estilo de textura',
    'design.roofing': 'Opciones de Techo',
    'design.selectRoofing': 'Elige tu estilo de techo',
    'design.flooring': 'Opciones de Piso',
    'design.vinylPlank': 'Tablón de Vinilo',
    'design.selectVinyl': 'Elige piso de tablón de vinilo',
    'design.carpetColor': 'Color de Alfombra',
    'design.selectCarpet': 'Elige alfombra para habitaciones',
    'design.ceramicTile': 'Azulejo Cerámico',
    'design.selectCeramic': 'Elige azulejo para piso de baño',
    'design.interiorPaint': 'Pintura Interior',
    'design.selectPaint': 'Elige color de pintura interior',
    'design.cabinetColor': 'Color de Gabinete',
    'design.selectCabinet': 'Elige estilo de gabinete de cocina',
    'design.countertop': 'Encimera',
    'design.selectCountertop': 'Elige material de encimera',
    'design.summary': 'Resumen de Diseño',
    'design.summaryDesc': 'Revisa tus selecciones',
    'design.notSelected': 'No seleccionado',
    'design.saveLater': 'Guardar para Después',
    'design.submit': 'Enviar Diseño y Obtener Cotización',
    'design.submitting': 'Guardando...',
    'design.footerNote': 'Una vez enviado, nuestro equipo revisará tu diseño y te contactará en 24-48 horas con una cotización detallada y cronograma.',
    'design.toast.incomplete': 'Selección incompleta',
    'design.toast.incompleteDesc': 'Por favor completa todas las opciones de diseño antes de continuar.',
    'design.toast.saved': '¡Diseño guardado!',
    'design.toast.savedDesc': 'Tus preferencias de diseño personalizadas se han guardado en tu cuenta.',
    'design.toast.error': 'Error al guardar diseño',
    'design.toast.errorDesc': 'Hubo un problema al guardar tu diseño. Por favor intenta de nuevo.',
    'design.toast.authRequired': 'Autenticación requerida',
    'design.toast.authRequiredDesc': 'Por favor inicia sesión para guardar tu diseño.',
    
    // Auth
    'auth.welcome': 'Bienvenido a Byrdson Services',
    'auth.signIn': 'Inicia sesión en tu cuenta',
    'auth.signUp': 'Crea una nueva cuenta',
    'auth.email': 'Correo Electrónico',
    'auth.password': 'Contraseña',
    'auth.signInButton': 'Iniciar Sesión',
    'auth.signUpButton': 'Registrarse',
    'auth.noAccount': '¿No tienes una cuenta?',
    'auth.hasAccount': '¿Ya tienes una cuenta?',
    'auth.signUpLink': 'Regístrate',
    'auth.signInLink': 'Inicia sesión',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Load saved language preference from localStorage
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage === 'EN' || savedLanguage === 'ES') ? savedLanguage : 'EN';
  });

  const updateLanguage = (lang: Language) => {
    setLanguage(lang);
    // Save language preference to localStorage
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.EN] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: updateLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
